import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { HR } from '../Base';
import Spacing from '../Spacing';
import FreeGiftAccordion from '../FreeGiftAccordion';
import PromoArea from '../PromoArea';
import FreeGiftIcon from '../../assets/svgs/gift.svg';
import ShippingMethodIcon from '../../assets/svgs/shippingicon.svg';

import sortCapacity from '../../helpers/sort-capacity';
import htmlStringToReactComponent from '../../helpers/htmlStringToReactComponent';
import { Divider } from '@dls/web';

const FreeGiftRow = styled.div`
  display: flex;
`;

const ShippingStyledIcon = styled.div`
  margin-top: ${({ theme }) => theme.spacing(0.5)};
  margin-right: ${({ theme }) => theme.spacing(1)};
`;

const StyledIcon = styled.div`
  margin-right: ${({ theme }) => theme.spacing(1)};
`;

const buildFreeGiftSubLiner = (freeGiftsBySku, deviceColours, deviceSizes) => {
  const sortName = arr => arr.sort((a, b) => a.localeCompare(b));
  const buildString = texts => texts.join(', ');
  const getCommonItems = (a, b) => a.filter(elem => b.includes(elem));
  const getUniqueItems = arr => Array.from(new Set(arr));

  const colours = deviceColours.map(colour => colour.name);
  const sizes = getUniqueItems(deviceSizes.map(size => size.size));
  const freeGiftColours = getUniqueItems(freeGiftsBySku.map(sku => sku.colour));
  const freeGiftSizes = getUniqueItems(freeGiftsBySku.map(sku => sku.size));

  const allColoursMatched = colours.length === freeGiftColours.length;
  const allSizesMatched = sizes.length === freeGiftSizes.length;

  let subLinerTitle = '';
  let contentArr = [];

  if (!allSizesMatched) {
    subLinerTitle = 'Applicable to: ';
    contentArr = sortCapacity(getCommonItems(sizes, freeGiftSizes));
  } else if (!allColoursMatched) {
    subLinerTitle = 'Applicable to: ';
    contentArr = sortName(getCommonItems(colours, freeGiftColours));
  }

  return `${subLinerTitle} ${buildString(contentArr)}`;
};

const PhoneDetailsPromo = props => {
  const {
    phoneSizes,
    phoneColours,
    freeGiftsByDevice,
    freeGiftsBySku,
    freeGiftStockAvail,
    promotions,
    shippingLiner
  } = props;

  const freeGiftsList = freeGiftsByDevice
    .filter(freeGift => freeGift.title)
    .map(freeGift => {
      const matched = freeGiftStockAvail.find(
        stock => stock.itemCode === freeGift.sku
      );
      // show unavailable only after api has been successfully called.
      const available = matched ? matched.availableQty > 0 : true;
      return {
        text: freeGift.title,
        value: freeGift.text,
        available: available // tie-in with stock availability
      };
    });

  const outOfStockCompletely =
    freeGiftsList.filter(freeGift => freeGift.available).length === 0;

  const freeGiftSubLiner = outOfStockCompletely
    ? ''
    : buildFreeGiftSubLiner(freeGiftsBySku, phoneColours, phoneSizes);

  return (
    <Spacing top={3}>
      {promotions && promotions.primaryLiner && (
        <Spacing top={2.5} bottom={2.5}>
          <PromoArea
            primaryLiner={promotions.primaryLiner}
            promotions={promotions.promotions}
            secondaryTitle={promotions.secondaryTitle}
            secondaryDescription={promotions.secondaryDescription}
            secondaryFooter={promotions.secondaryFooter}
          />
        </Spacing>
      )}
      <Spacing top={3} bottom={3}>
        {shippingLiner && (
          <FreeGiftRow>
            <ShippingStyledIcon>
              <ShippingMethodIcon />
            </ShippingStyledIcon>
            <FreeGiftAccordion
              title="Shipping Method"
              description={htmlStringToReactComponent(shippingLiner)}
            />
          </FreeGiftRow>
        )}
        {freeGiftsList.length > 0 && (
          <FreeGiftRow>
            <StyledIcon>
              <FreeGiftIcon />
            </StyledIcon>
            <FreeGiftAccordion
              title="Free gift(s)"
              items={freeGiftsList}
              description={freeGiftSubLiner}
            />
          </FreeGiftRow>
        )}
      </Spacing>
      <Spacing top={6} topMd={6}>
        <Divider />
      </Spacing>
    </Spacing>
  );
};

PhoneDetailsPromo.defaultProps = {
  phoneSizes: [],
  phoneColours: [],
  freeGiftsByDevice: [],
  freeGiftsBySku: [],
  freeGiftStockAvail: [],
  shippingLiner: ''
};

PhoneDetailsPromo.propTypes = {
  phoneSizes: PropTypes.array,
  phoneColours: PropTypes.array,
  freeGiftsByDevice: PropTypes.array,
  freeGiftsBySku: PropTypes.array,
  freeGiftStockAvail: PropTypes.array,
  /** Sets the promotion object */
  promotions: PropTypes.object,
  /** Shipping Liner of product */
  shippingLiner: PropTypes.string
};

export default PhoneDetailsPromo;
